import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import VendorCard from '../components/VendorCard'
import VendorDetail from '../components/VendorDetail'

export default function Dashboard() {
  const { profile } = useAuth()
  const [vendors, setVendors] = useState([])
  const [loading, setLoading] = useState(true)
  const [detailVendor, setDetailVendor] = useState(null)

  useEffect(() => {
    if (profile?.lat && profile?.lng) {
      fetchNearbyVendors()
    } else {
      setLoading(false)
    }
  }, [profile])

  const fetchNearbyVendors = async () => {
    try {
      const response = await fetch('/.netlify/functions/get-nearby', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lat: profile.lat,
          lng: profile.lng,
          radius_miles: profile.radius_miles || 1,
          transport_mode: profile.transport_mode || 'walking'
        })
      })

      if (response.ok) {
        const data = await response.json()
        const schedules = data?.schedules || []

        // Group by vendor
        const vendorMap = new Map()
        for (const schedule of schedules) {
          const key = schedule.applicant
          if (!vendorMap.has(key)) {
            vendorMap.set(key, {
              id: key,
              applicant: schedule.applicant,
              vendor_permit: schedule.vendor_permit,
              vendors: schedule.vendors,
              facility_type: schedule.facility_type,
              schedules: [],
              distance_miles: schedule.distance_miles,
              lat: schedule.lat,
              lng: schedule.lng
            })
          }

          const vendor = vendorMap.get(key)
          vendor.schedules.push(schedule)

          // Keep closest distance
          if (schedule.distance_miles < vendor.distance_miles) {
            vendor.distance_miles = schedule.distance_miles
            vendor.lat = schedule.lat
            vendor.lng = schedule.lng
          }
        }

        const vendorList = Array.from(vendorMap.values())
          .sort((a, b) => a.distance_miles - b.distance_miles)

        setVendors(vendorList)
      }
    } catch (error) {
      console.error('Error fetching nearby vendors:', error)
    } finally {
      setLoading(false)
    }
  }

  // No location set - prompt to set up
  if (!profile?.lat || !profile?.lng) {
    return (
      <div className="page page-narrow" style={{ textAlign: 'center', paddingTop: '80px' }}>
        <div className="empty-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
        </div>
        <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px', marginTop: '24px' }}>
          Set Your Location
        </h1>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
          To see nearby food vendors and receive notifications, you need to set your address first.
        </p>
        <Link to="/settings" className="btn btn-primary btn-lg">
          Set Up Location
        </Link>
      </div>
    )
  }

  // Count schedules for today
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const today = days[new Date().getDay()]
  const todaySchedules = vendors.reduce((count, v) =>
    count + v.schedules.filter(s => s.day_of_week === today).length, 0
  )
  const totalSchedules = vendors.reduce((count, v) => count + v.schedules.length, 0)

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">My Feed</h1>
        <p className="page-subtitle">
          Food vendors within {profile.radius_miles || 1} mile{(profile.radius_miles || 1) !== 1 ? 's' : ''} of your location
        </p>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}>
          <div className="spinner"></div>
        </div>
      ) : vendors.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
          <div className="empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.3-4.3"/>
            </svg>
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', marginTop: '16px' }}>
            No vendors found nearby
          </h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
            Try expanding your search radius or explore the full map.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <Link to="/settings" className="btn btn-secondary">
              Adjust Radius
            </Link>
            <Link to="/explore" className="btn btn-primary">
              Explore Map
            </Link>
          </div>
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="dashboard-stats">
            <div className="stat-card">
              <div className="stat-label">Vendors Nearby</div>
              <div className="stat-value primary">{vendors.length}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Schedule Slots</div>
              <div className="stat-value">{totalSchedules}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Available Today</div>
              <div className="stat-value">{todaySchedules}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Your Radius</div>
              <div className="stat-value">{profile.radius_miles || 1} mi</div>
            </div>
          </div>

          {/* Vendor List */}
          <div className="vendor-list">
            {vendors.map((vendor) => (
              <VendorCard
                key={vendor.id}
                vendor={vendor}
                onClick={() => setDetailVendor(vendor)}
              />
            ))}
          </div>
        </>
      )}

      {detailVendor && (
        <VendorDetail
          vendor={detailVendor}
          onClose={() => setDetailVendor(null)}
        />
      )}
    </div>
  )
}
